import {Drawer} from 'components/customer/Product/entity/Drawer';
import {DrawerRunner} from 'components/customer/Product/entity/DrawerRunner';
import {toNumber} from 'components/customer/Product/Drawer/helpers';

// Each drawer has a slot, and its face height is the slot's length: the slot
// starts under the drawer by its bottom clearance (the bottom drawer by its
// inner bottom clearance) and ends where the slot above starts, the top one
// at the height the layout shares out. The layout lays drawers out this way
// and a move keeps it. A drawer placed in a free space takes the top of one
// slot, so nothing else moves. A slot can be longer than its face, where a
// removed drawer's space went to no one; it is still free.

export interface Clearances {
    top: number;
    bottom: number;
    innerBottom: number;
}

export interface FreeSpace {
    // where the new drawer goes in the list, top first
    index: number;
    position: number;
    runner: DrawerRunner;
    faceHeight: number;
    // the drawer whose slot it shares, as listed now, and its face after
    neighbour: {index: number; faceHeight: number};
}

interface SpacesInput {
    // the drawers within the count, top first
    drawers: Drawer[];
    cabinetHeight: number;
    // what validation takes off the height above the top drawer
    topPart: number;
    // the height the layout shares out, where the top slot ends
    availableHeight: number;
    // up to whatever closes the top, as the drawing shows it
    usableHeight: number;
    // the new drawer's type
    clearances: Clearances;
    runners: DrawerRunner[];
}

const position = (drawer: Drawer) => toNumber(drawer.position_to_bottom);
const height = (drawer: Drawer) => toNumber(drawer.drawer_runner_height);
const top = (drawer: Drawer) => toNumber(drawer.systemTopClearance);
const bottom = (drawer: Drawer) => toNumber(drawer.systemBottomClearance);
const innerBottom = (drawer: Drawer) =>
    toNumber(drawer.systemInnerDrawerBottomClearance);
const needs = (drawer: Drawer) =>
    height(drawer) + top(drawer) + innerBottom(drawer);

const slotStart = (drawer: Drawer, isBottom: boolean) =>
    position(drawer) - (isBottom ? innerBottom(drawer) : bottom(drawer));

const upwards = (value: number) => Math.ceil(value * 100 - 1e-6) / 100;

// Tallest runner first, the lowest position it has room at. The position is
// kept where the runner lookup offers no taller runner, so the drawer ends up
// on the runner its space shows. Validation's ranges hold on both sides.
const firstFit = (
    runners: DrawerRunner[],
    range: (
        runner: DrawerRunner,
        taller: DrawerRunner
    ) => {lowest: number; highest: number}
) => {
    for (let at = 0; at < runners.length; at++) {
        const {lowest, highest} = range(runners[Number(at)], runners[at - 1]);
        const place = upwards(lowest);

        if (place <= highest + 1e-9) {
            return {runner: runners[Number(at)], position: place};
        }
    }

    return null;
};

export const freeSpaces = ({
    drawers,
    cabinetHeight,
    topPart,
    availableHeight,
    usableHeight,
    clearances,
    runners,
}: SpacesInput): FreeSpace[] => {
    const last = drawers.length - 1;
    const slotEnd = (index: number) =>
        index === 0
            ? availableHeight
            : slotStart(drawers[index - 1], index - 1 === last);
    // each height comes in several lengths; the lookup takes the first listed
    const tallestFirst = [...runners]
        .sort((a, b) => b.runnerHeight - a.runnerHeight)
        .filter(
            (runner, at, list) =>
                at === 0 || list[at - 1].runnerHeight !== runner.runnerHeight
        );
    const needsOf = (runner: DrawerRunner) =>
        runner.runnerHeight + clearances.top + clearances.innerBottom;
    // the lookup would offer the taller one too while its need fits
    const belowTaller = (
        taller: DrawerRunner,
        slotEnd: number,
        offset: number
    ) => (taller ? slotEnd + offset - needsOf(taller) + 0.01 : -Infinity);

    const spaces: FreeSpace[] = [];

    // above each drawer, in the top of its slot
    drawers.forEach((below, index) => {
        const above = drawers[index - 1];
        const start = slotStart(below, index === last);
        const end = slotEnd(index);

        const fit = firstFit(tallestFirst, (runner, taller) => ({
            lowest: Math.max(
                position(below) +
                    clearances.innerBottom +
                    top(below) +
                    height(below),
                // the drawer below keeps its runner
                start + needs(below) + clearances.bottom,
                belowTaller(taller, end, clearances.bottom)
            ),
            highest: Math.min(
                end + clearances.bottom - needsOf(runner),
                above
                    ? position(above) -
                          (innerBottom(above) +
                              clearances.top +
                              runner.runnerHeight)
                    : Math.min(
                          cabinetHeight -
                              (topPart + clearances.top + runner.runnerHeight),
                          usableHeight - (clearances.top + runner.runnerHeight)
                      )
            ),
        }));

        if (fit) {
            spaces.push({
                index,
                ...fit,
                faceHeight: end - (fit.position - clearances.bottom),
                neighbour: {
                    index,
                    faceHeight: fit.position - clearances.bottom - start,
                },
            });
        }
    });

    // under the bottom drawer, which then sits on its bottom clearance
    const lowest = drawers[Number(last)];

    if (lowest) {
        const end = position(lowest) - bottom(lowest);

        const fit = firstFit(tallestFirst, (runner, taller) => ({
            lowest: Math.max(
                clearances.innerBottom,
                belowTaller(taller, end, clearances.innerBottom)
            ),
            highest: Math.min(
                end + clearances.innerBottom - needsOf(runner),
                position(lowest) -
                    (innerBottom(lowest) + clearances.top + runner.runnerHeight)
            ),
        }));

        if (fit) {
            spaces.push({
                index: drawers.length,
                ...fit,
                faceHeight: end - (fit.position - clearances.innerBottom),
                neighbour: {index: last, faceHeight: slotEnd(last) - end},
            });
        }
    }

    return spaces;
};

// The new drawer takes the top of the slot below it, which keeps the rest;
// under the bottom drawer, that drawer's slot then starts at its bottom
// clearance. All of them count as set by hand, so a count change leaves them
// where they are.
export const insertDrawer = (
    drawers: Drawer[],
    space: FreeSpace,
    drawer: Drawer
): Drawer[] => {
    const next: Drawer[] = drawers.map((held) => ({...held, touched: true}));
    const added: Drawer = {
        ...drawer,
        position_to_bottom: space.position,
        drawer_face_height: space.faceHeight,
        touched: true,
    };

    const {neighbour} = space;

    next[neighbour.index] = {
        ...next[neighbour.index],
        drawer_face_height: neighbour.faceHeight,
    };
    next.splice(space.index, 0, added);

    return next;
};
